import { FEATURE } from '@/constants';
import { useWebpackChunkHandler } from '@/composables/useWebpackChunkHandler';

const { handleChunkLoadError } = useWebpackChunkHandler();

const ROLE = {
  teacher: 'teacher',
  designer: 'designer',
  learner: 'learner',
  reviewer: 'reviewer',
  issuer: 'issuer'
};

const MODE = {
  verify: 'VERIFY',
  learn_verify: 'LEARN_VERIFY'
};

export const routes = [
  {
    path: '/pdpa',
    name: 'PDPA',
    component: () =>
      handleChunkLoadError(() =>
        import(/* webpackChunkName: "pdpa" */ '../views/PDPA/PagePDPA.vue')
      )
  },
  {
    path: '/login',
    name: 'SignIn',
    // Login is handled by the catalog (Laravel) app. Redirect any navigation to
    // /login out to the catalog's /login page instead of rendering the legacy
    // in-app SignInV2 view. Forward `previousPath` so the catalog sends the user
    // back to where they came from after signing in.
    beforeEnter(to) {
      const target = new URL('/login', process.env.VUE_APP_CATALOG_APP_URL);
      const { previousPath } = to.query;
      if (previousPath) {
        target.searchParams.set('previousPath', previousPath);
      }
      window.location.href = target.toString();
    },
    component: () =>
      handleChunkLoadError(() =>
        import(
          /* webpackChunkName: "auth--signin" */
          '../views/Authentication/SignInV2.vue'
        )
      ),
    meta: { authenticated: false }
  },
  {
    path: '/legend-login',
    name: 'SignIn',
    component: () =>
      handleChunkLoadError(() =>
        import(
          /* webpackChunkName: "auth--signin" */
          '../views/Authentication/SignInV2.vue'
        )
      ),
    meta: { authenticated: false }
  },
  {
    path: '/logout',
    name: 'Logout',
    component: () =>
      handleChunkLoadError(() =>
        import(
          /* webpackChunkName: "auth--logout" */
          '../views/Authentication/PageLogout.vue'
        )
      ),
    meta: { isPublic: true }
  },
  {
    path: '/sign-up',
    name: 'SignUp',
    // Sign-up is handled by the catalog (Laravel) app. Redirect any navigation
    // to /sign-up out to the catalog's /register page instead of rendering the
    // legacy in-app SignUpFlow.
    beforeEnter() {
      window.location.href = new URL('/register', process.env.VUE_APP_CATALOG_APP_URL).toString();
    },
    component: () =>
      handleChunkLoadError(() =>
        import(
          /* webpackChunkName: "auth--signup" */
          '../views/Authentication/SignUpFlow.vue'
        )
      ),
    meta: { authenticated: false }
  },

  {
    path: '/auth-callback',
    name: 'AuthCallBack',
    component: () =>
      handleChunkLoadError(() =>
        import(
          /* webpackChunkName: "auth--PageAuthCallBack" */
          '../views/Authentication/PageAuthCallback.vue'
        )
      ),
    meta: { isPublic: true }
  },
  {
    path: '/send-verify-email',
    name: 'send-verify-email',
    component: () =>
      handleChunkLoadError(() =>
        import(
          /* webpackChunkName: "auth--verify-email" */
          '../views/VerifyEmail/PageSendVerifyEmail.vue'
        )
      ),
    meta: { authorize: ['*'] }
  },
  {
    path: '/verify-email',
    name: 'verify-email',
    component: () =>
      handleChunkLoadError(() =>
        import(
          /* webpackChunkName: "auth--verify" */
          '../views/VerifyEmail/PageVerifyEmail.vue'
        )
      ),
    meta: { isPublic: true }
  },
  {
    path: '/resend-verify-email',
    name: 'resend-verify-email',
    component: () =>
      handleChunkLoadError(() =>
        import(
          /* webpackChunkName: "auth--verify" */
          '../views/ChangeRequest/PageResendEmail.vue'
        )
      ),
    meta: { isPublic: true }
  },
  {
    path: '/profile',
    name: 'profile',
    component: () =>
      handleChunkLoadError(() =>
        import(
          /* webpackChunkName: "auth--profile" */
          '../views/Profile/PageUserProfile.vue'
        )
      ),
    meta: { authorize: ['*'] }
  },
  {
    path: '/profile/edit',
    name: 'profile/edit',
    component: () =>
      handleChunkLoadError(() =>
        import(
          /* webpackChunkName: "auth--profile-edit" */
          '../views/Profile/PageEditProfile.vue'
        )
      ),
    meta: { authorize: ['*'] }
  },
  {
    path: '/profile/basic-info/edit',
    name: 'profile/basic-info/edit',
    component: () =>
      handleChunkLoadError(() =>
        import(
          /* webpackChunkName: "auth--profile-basicinfo-edit" */
          '../views/Profile/PageEditProfileBasicInfo.vue'
        )
      ),
    meta: { authorize: ['*'] }
  },
  {
    path: '/profile/edit-name',
    name: 'profile/edit-name',
    component: () =>
      handleChunkLoadError(() =>
        import(
          /* webpackChunkName: "auth--profile-basicinfo-edit" */
          '../views/Profile/PageEditProfileName.vue'
        )
      ),
    meta: { authorize: ['*'] }
  },
  {
    path: '/profile/edit-email',
    name: 'profile/edit-email',
    component: () =>
      handleChunkLoadError(() =>
        import(
          /* webpackChunkName: "auth--profile-basicinfo-edit-email" */
          '../views/Profile/PageEditProfileEmail.vue'
        )
      ),
    meta: { authorize: ['*'], featureFlag: [FEATURE.change_email] }
  },
  {
    path: '/learner/:learnerId',
    name: 'learner',
    component: () =>
      handleChunkLoadError(() =>
        import(
          /* webpackChunkName: "learner--profile" */
          '../views/Profile/PageLearnerProfile.vue'
        )
      ),
    meta: { authorize: ['*'] }
  },
  {
    path: '/',
    name: 'learning-unit',
    component: () =>
      handleChunkLoadError(() =>
        import(
          /* webpackChunkName: "learning-unit--home" */
          '../views/LearningUnit/PageLearningUnit'
        )
      ),
    meta: { isPublic: true }
  },
  // {
  //   path: "/learning-unit/info/:learningUnitId(\\d+)", //deprecaded
  //   name: "learning-unit/info",
  //   component: () =>
  //     import(
  //       /* webpackChunkName: "learning-unit--info" */
  //       "../views/LearningUnitExplore/PageExploreInfo"
  //     ),
  //     meta: { isPublic: true },
  // },
  // {
  //   path: '/explore-v2',
  //   name: 'ExploreV2',
  //   component: () =>
  //     handleChunkLoadError(() =>
  //       import(/* webpackChunkName: "explore-v2" */ '../views/LearningUnit/PageExploreV2.vue')
  //     ),
  //   meta: { isPublic: true }
  // },
  {
    path: '/search-result',
    name: 'search-results',
    component: () =>
      handleChunkLoadError(() =>
        import(/* webpackChunkName: "search-result" */ '../views/LearningUnit/PageSearchResult.vue')
      ),
    meta: { isPublic: true }
  },
  {
    path: '/micro-credentials/detail/:learningUnitId(\\d+)',
    name: 'micro-credentials/detail',
    component: () =>
      handleChunkLoadError(() =>
        import(
          /* webpackChunkName: "micro-credentials--detail" */
          '../views/LearningUnitExplore/PageExploreDetail'
        )
      ),
    meta: { isPublic: true }
  },
  // {
  //   path: "/learning-unit/my-learning-unit",
  //   name: "learning-unit/my-learning-unit",
  //   component: () =>
  //     import(
  //       /* webpackChunkName: "learning-unit--my-list" */
  //       "../views/MyLearningUnit/PageMyLearningUnit"
  //     ),
  //   meta: { authorize: [ROLE.learner, ROLE.teacher, ROLE.designer] },
  // },
  // {
  //   path: "/learning-unit/:learningUnitName", //deprecaded
  //   name: "learning-unit/info/name",
  //   component: () =>
  //     import(
  //       /* webpackChunkName: "learning-unit--explore" */
  //       "../views/LearningUnitExplore/PageExploreInfo"
  //     ),
  // },
  // {
  //   path: "/learning-unit/:learningUnitId/learning-feed/:sectionId",
  //   name: "learning-unit/feed",
  //   component: () =>
  //     import(
  //       /* webpackChunkName: "learning-unit--learning-feed" */ "../views/Feed/PageLearningFeed"
  //     ),
  //   meta: { authorize: [ROLE.learner, ROLE.teacher, ROLE.designer] },
  // },
  {
    path: '/learning-unit/:learningUnitId/section/:sectionId/assessment-submission/:postId',
    name: 'learnerResponseSubmission',
    component: () =>
      handleChunkLoadError(() =>
        import(
          /* webpackChunkName: "learning-unit--submission" */
          '../views/Submission/PageLearnerSubmission'
        )
      ),
    meta: { authorize: [ROLE.learner, ROLE.teacher, ROLE.designer] }
  },
  {
    path: '/learning-unit/:learningUnitId/section/:sectionId/review-submission/:postId',
    name: 'TeachingReviewSubmission',
    component: () =>
      handleChunkLoadError(() =>
        import(
          /* webpackChunkName: "learning-unit--review-submission" */
          '../views/Submission/PageTeachingReview'
        )
      ),
    meta: { authorize: [ROLE.learner, ROLE.teacher, ROLE.designer] }
  },
  {
    path: '/design-planning',
    name: 'design-planning',
    component: () =>
      handleChunkLoadError(() =>
        import(
          /* webpackChunkName: "learning-unit--design-planing" */
          '../views/LearningUnitDesignAndPlanning/PageDesignAndPlanning'
        )
      ),
    meta: { authorize: [ROLE.teacher, ROLE.designer] }
  },
  {
    path: '/design-planning/create-mc',
    name: 'design-planning/create-mc',
    component: () =>
      handleChunkLoadError(() =>
        import(
          /* webpackChunkName: "Micro-Credential--create" */
          '../views/LearningUnit/PageCreateMicroCredential'
        )
      ),
    meta: { authorize: [ROLE.teacher, ROLE.designer, ROLE.reviewer] }
  },
  {
    path: '/design-planning/edit-mc/:learningUnitId',
    name: 'design-planning/edit-mc',
    component: () =>
      handleChunkLoadError(() =>
        import(
          /* webpackChunkName: "Micro-Credential--edit" */
          '../views/LearningUnit/PageEditMicroCredential'
        )
      ),
    meta: {
      authorize: [ROLE.teacher, ROLE.designer, ROLE.reviewer],
      learningUnit: { isDraftOnly: true }
    }
  },

  {
    path: '/design-planning/info/:learningUnitId',
    name: 'design-planning/info',
    component: () =>
      handleChunkLoadError(() =>
        import(
          /* webpackChunkName: "learning-unit--detail" */
          '../views/LearningUnitInfo/PageLearningUnitInfo'
        )
      ),
    meta: {
      authorize: [ROLE.learner, ROLE.teacher, ROLE.designer, ROLE.reviewer],
      learningUnit: { isMember: true },
      redirectPrevious: true
    }
  },
  // {
  //   path: "/design-planning/learning-outcome/create/:learningUnitId",
  //   name: "design-planning/learning-outcome/create",
  //   component: () =>
  //     import( /* webpackChunkName: "learning-unit--manage-lo" */ "../views/LearningOutcome/PageManageLearningOutcome"),
  //   meta: { authorize: [ROLE.teacher, ROLE.designer, ROLE.reviewer] },
  // },
  {
    path: '/design-planning/:learningUnitId/ultimate-learning-outcome',
    name: 'design-planning/ultimate-learning-outcome/create',
    component: () =>
      handleChunkLoadError(() =>
        import(
          /* webpackChunkName: "learning-unit--manage-ulo" */
          '../views/LearningOutcome/PageManageUltimateLearningOutcome'
        )
      ),
    meta: { authorize: [ROLE.teacher, ROLE.designer, ROLE.reviewer] }
  },
  {
    path: '/design-planning/:learningUnitId/ultimate-learning-outcome/:id',
    name: 'design-planning/ultimate-learning-outcome/edit',
    component: () =>
      handleChunkLoadError(() =>
        import(
          /* webpackChunkName: "learning-unit--manage-ulo" */
          '../views/LearningOutcome/PageManageUltimateLearningOutcome'
        )
      ),
    meta: {
      authorize: [ROLE.teacher, ROLE.designer, ROLE.reviewer],
      learningUnit: {
        onlyDraft: true
      }
    }
  },
  // {
  //   path: "/design-planning/learning-outcome/edit/:learningUnitId/:learningOutcomeId",
  //   name: "design-planning/learning-outcome/edit",
  //   component: () =>
  //     import( /* webpackChunkName: "learning-unit--manage-lo" */ "../views/LearningOutcome/PageManageLearningOutcome"),
  //   meta: { authorize: [ROLE.teacher, ROLE.designer, ROLE.reviewer] },
  // },
  // {
  //     path: "/design-planning/section/create/:learningUnitId",
  //     name: "design-planning/section/create",
  //     component: () =>
  //         import (
  //             /* webpackChunkName: "learning-unit--manage-section" */
  //             "../views/Section/PageManageSection"
  //         ),
  //     meta: { authorize: [ROLE.teacher, ROLE.designer, ROLE.reviewer] },
  // },
  // {
  //     path: "/design-planning/section/edit/:learningUnitId/:sectionId",
  //     name: "design-planning/section/edit",
  //     component: () =>
  //         import (
  //             /* webpackChunkName: "learning-unit--manage-section" */
  //             "../views/Section/PageManageSection"
  //         ),
  //     meta: { authorize: [ROLE.teacher, ROLE.designer, ROLE.reviewer] },
  // },
  {
    path: '/design-planning/:learningUnitId/teaching-feed/:sectionId',
    name: 'design-planning/teaching-feed',
    component: () =>
      handleChunkLoadError(() =>
        import(
          /* webpackChunkName: "learning-unit--teaching-feed" */
          '../views/Feed/PageTeachingFeed'
        )
      ),
    meta: { authorize: ['*'] }
  },
  {
    path: '/design-planning/:learningUnitId/method-components',
    name: 'design-planning/method-components',
    component: () =>
      handleChunkLoadError(() =>
        import(
          /* webpackChunkName: "learning-unit--method-components" */
          '../views/LearningUnitMethods/PageCreateMethodComponent.vue'
        )
      ),
    meta: {
      authorize: [ROLE.designer, ROLE.reviewer, ROLE.learner]
    }
  },
  {
    path: '/learning-unit/:learningUnitId/evaluation-assessment/:part',
    name: 'learning-unit/evaluation-assessment/create',
    component: () =>
      handleChunkLoadError(() =>
        import(
          /* webpackChunkName: "learning-unit--evidence-submission-manage" */
          '../views/EvaluationAssessment/PageCreateOrEditEvaluationAssessment'
        )
      ),
    meta: { authorize: [ROLE.teacher, ROLE.designer, ROLE.reviewer] }
  },
  {
    path: '/learning-unit/:learningUnitId/evaluation-assessment/:part/:postId',
    name: 'learning-unit/evaluation-assessment/edit',
    component: () =>
      handleChunkLoadError(() =>
        import(
          /* webpackChunkName: "learning-unit--evidence-submission-manage" */
          '../views/EvaluationAssessment/PageCreateOrEditEvaluationAssessment'
        )
      ),
    meta: { authorize: [ROLE.teacher, ROLE.designer, ROLE.reviewer] }
  },
  {
    path: '/learning-unit/:learningUnitId/part/:partType/description',
    name: 'learning-unit/part/description',
    component: () =>
      handleChunkLoadError(() =>
        import(
          /* webpackChunkName: "learning-unit--evaluation-assessment-description" */
          '../views/EvaluationAssessment/PageEditPartDescription'
        )
      ),
    meta: { authorize: [ROLE.teacher, ROLE.designer, ROLE.reviewer] }
  },
  {
    path: '/learning-unit/:learningUnitId/score-description',
    name: 'learning-unit/score-description',
    component: () =>
      handleChunkLoadError(() =>
        import(
          /* webpackChunkName: "learning-unit--evaluation-assessment-description" */
          '../views/EvaluationAssessment/PageEditScoreDescription'
        )
      ),
    meta: { authorize: [ROLE.teacher, ROLE.designer, ROLE.reviewer] }
  },
  {
    path: '/learning-unit/:learningUnitId',
    name: 'learning-unit-index',
    component: () =>
      handleChunkLoadError(() =>
        import(
          /* webpackChunkName: "learning-unit--main" */
          '../views/LearningUnit/PageIndex'
        )
      ),
    children: [
      {
        path: 'verify',
        name: 'learning-unit/verify',
        component: () =>
          handleChunkLoadError(() =>
            import(
              /* webpackChunkName: "learning-unit--verify" */
              '../views/EvidenceSubmission/PageSubmitEvidence.vue'
            )
          ),
        meta: { redirectPrevious: true }
      },
      {
        // UserProfile will be rendered inside User's <router-view>
        // when /user/:id/profile is matched
        path: 'evidence/submit',
        name: 'learning-unit/evidence/submit',
        component: () =>
          import(
            /* webpackChunkName: "learning-unit--evidence-submission-submit" */
            '../views/EvidenceSubmission/PageSubmitEvidence'
          )
      },
      {
        path: 'learning-feed/:sectionId',
        name: 'learning-unit/feed',
        component: () =>
          handleChunkLoadError(() =>
            import(
              /* webpackChunkName: "learning-unit--learning-feed" */
              '../views/Feed/PageLearningFeed'
            )
          )
        // meta: { authorize: [ROLE.learner, ROLE.teacher, ROLE.designer] },
      },
      {
        path: 'submission-denied/:evidenceSubmissionId',
        name: 'learning-unit/submission-denied',
        component: () =>
          handleChunkLoadError(() =>
            import(
              /* webpackChunkName: "learning-unit--submission-denied" */
              '../views/EvidenceSubmission/PageOverallSubmissionDenied.vue'
            )
          ),
        meta: { authorize: [ROLE.learner] }
      }
    ]
    // meta: { authorize: [ROLE.learner] },
  },
  {
    path: '/micro-credentials/:learningUnitId',
    name: 'learning-unit-index-v2',
    component: () =>
      handleChunkLoadError(() =>
        import(
          /* webpackChunkName: "learning-unit--main" */
          '../views/LearningUnit/PageIndexV2'
        )
      ),
    children: [
      {
        path: 'method-components',
        name: 'learning-unit/method-components',
        component: () =>
          handleChunkLoadError(() =>
            import(
              /* webpackChunkName: "learning-unit--method-components" */
              '../views/VerifyCompetency/PageMethodComponent.vue'
            )
          )
      },
      {
        path: 'references',
        name: 'learning-unit/references',
        component: () =>
          handleChunkLoadError(() =>
            import(
              /* webpackChunkName: "learning-unit--references" */
              '../views/VerifyCompetency/PageReference.vue'
            )
          )
      }
    ]
  },
  {
    path: '/micro-credentials/:learningUnitId',
    name: 'learning-unit-preview',
    props: { isPreview: true },
    component: () =>
      handleChunkLoadError(() =>
        import(
          /* webpackChunkName: "learning-unit--main" */
          '../views/LearningUnit/PageIndexV2'
        )
      ),
    meta: {
      authorize: [ROLE.designer, ROLE.learner],
      learningUnit: { isMember: true },
      layout: 'no'
    },
    children: [
      {
        path: 'method-components/preview',
        name: 'learning-unit/method-components-preview',
        props: { isPreview: true },
        component: () =>
          handleChunkLoadError(() =>
            import(
              /* webpackChunkName: "learning-unit--method-components" */
              '../views/VerifyCompetency/PageMethodComponent.vue'
            )
          ),
        meta: {
          authorize: [ROLE.designer, ROLE.learner],
          learningUnit: { isMember: true },
          layout: 'no'
        }
      },
      {
        path: 'references/preview',
        name: 'learning-unit/references-preview',
        props: { isPreview: true },
        component: () =>
          handleChunkLoadError(() =>
            import(
              /* webpackChunkName: "learning-unit--references" */
              '../views/VerifyCompetency/PageReference.vue'
            )
          ),
        meta: {
          authorize: [ROLE.designer, ROLE.learner],
          learningUnit: { isMember: true },
          layout: 'no'
        }
      }
    ]
  },
  {
    path: '/learning-unit/:learningUnitId/learner/:learnerId/evidence/:evidenceSubmissionId/assess',
    name: 'learning-unit/evidence/assess',
    component: () =>
      handleChunkLoadError(() =>
        import(
          /* webpackChunkName: "learning-unit--evidence-submission-assess" */
          '../views/EvidenceSubmission/PageAssessEvidence'
        )
      ),
    meta: {
      learningUnit: { isMember: true },
      redirectPrevious: true
    }
  },

  {
    path: '/learning-unit/:learningUnitId/preview',
    name: 'learning-unit/preview',
    component: () =>
      handleChunkLoadError(() =>
        import(
          /* webpackChunkName: "learning-unit--evidence-submission-assess" */
          '../views/EvidenceSubmission/PagePreviewEvidence'
        )
      ),
    meta: {
      learningUnit: { isMember: true },
      redirectPrevious: true
    }
  },
  {
    path: '/learning-unit/:learningUnitId/learner/:learnerId/evidence/:evidenceSubmissionId/grant',
    name: 'learning-unit/evidence/grant',
    component: () =>
      handleChunkLoadError(() =>
        import(
          /* webpackChunkName: "learning-unit--evidence-submission-assess" */
          '../views/EvidenceSubmission/PageGrantEvidence'
        )
      ),
    meta: { authorize: [ROLE.issuer], redirectPrevious: true }
  },

  {
    path: '/learning-unit/:learningUnitId/learner/:learnerId/evidence/:evidenceSubmissionId/view',
    name: 'learning-unit/evidence/view',
    component: () =>
      handleChunkLoadError(() =>
        import(
          /* webpackChunkName: "learning-unit--evidence-submission-view" */
          '../views/EvidenceSubmission/PageViewEvidence'
        )
      ),
    meta: {
      learningUnit: { isMember: true },
      redirectPrevious: true
    }
  },
  // {
  //   path: "/design-planning/activity/create/:learningUnitId",
  //   name: "design-planning/activity/create",
  //   component: () => import("../views/Activity/PageManageActivity"),
  //   meta: { authorize: [ROLE.teacher, ROLE.designer, ROLE.reviewer] },
  // },
  // {
  //   path: "/design-planning/activity/edit/:learningUnitId/:activityId",
  //   name: "design-planning/activity/edit",
  //   component: () => import("../views/Activity/PageManageActivity"),
  //   meta: { authorize: [ROLE.teacher, ROLE.designer, ROLE.reviewer] },
  // },
  {
    path: '/design-planning/learning-unit/:learningUnitId/section/:sectionId/post/create',
    name: 'design-planning/post/create',
    component: () =>
      handleChunkLoadError(() =>
        import(
          /* webpackChunkName: "learning-unit--manage-post" */
          '../views/Post/PageManagePost'
        )
      ),
    meta: { authorize: [ROLE.teacher, ROLE.designer, ROLE.reviewer] }
  },
  {
    path: '/design-planning/learning-unit/:learningUnitId/section/:sectionId/post/:postId/edit',
    name: 'design-planning/post/edit',
    component: () =>
      handleChunkLoadError(() =>
        import(
          /* webpackChunkName: "learning-unit--manage-post" */
          '../views/Post/PageManagePost'
        )
      ),
    meta: { authorize: [ROLE.teacher, ROLE.designer, ROLE.reviewer] }
  },
  {
    path: '/design-planning/learning-unit/:learningUnitId/section/:sectionId/post/:postId/edit-video-conferance',
    name: 'design-planning/post/edit-video-conference',
    component: () =>
      handleChunkLoadError(() =>
        import(
          /* webpackChunkName: "learning-unit--manage-meeting-post" */
          '../views/Post/PageManagePost'
        )
      ),
    meta: { authorize: [ROLE.teacher, ROLE.designer, ROLE.reviewer] }
  },
  {
    path: '/teaching',
    name: 'teaching',
    component: () =>
      handleChunkLoadError(() =>
        import(
          /* webpackChunkName: "learning-unit--live-teaching" */
          '../views/Teaching/PageTeaching'
        )
      ),
    meta: { authorize: [ROLE.teacher] }
  },
  {
    path: '/evaluate',
    name: 'evaluate',
    component: () =>
      handleChunkLoadError(() =>
        import(
          /* webpackChunkName: "learning-unit--evaluate" */
          '../views/Evaluate/PageEvaluate.vue'
        )
      ),
    meta: {
      // authorize: [ROLE.teacher, ROLE.designer],
      authorize: [],
      redirectPrevious: true
    }
  },
  // {
  //   path: "/example/show-criterion",
  //   name: "example/show-criterion",
  //   component: () => import("./examples/ShowCriterion"),
  // },
  {
    path: '/learning-unit/:learningUnitId/section/:sectionId/video-conference/:postId/teacher',
    name: 'learning-unit/video-conference/teacher',
    component: () =>
      handleChunkLoadError(() =>
        import(
          /* webpackChunkName: "learning-unit--meeting-teacher" */
          '../views/VideoConference/PageTeacherVideoConference'
        )
      ),
    meta: { authorize: [ROLE.teacher] }
  },
  {
    path: '/learning-unit/:learningUnitId/section/:sectionId/video-conference/:postId/learner',
    name: 'learning-unit/video-conference/learner',
    component: () =>
      handleChunkLoadError(() =>
        import(
          /* webpackChunkName: "learning-unit--meeting-learner" */
          '../views/VideoConference/PageLearnerVideoConference'
        )
      ),
    meta: { authorize: [ROLE.learner, ROLE.teacher, ROLE.designer] }
  },
  // {
  //   path: '/learning-unit/:learningUnitId/section/:sectionId/grant-certificate/teacher',
  //   name: 'learning-unit/grant-certificate/teacher',
  //   component: () =>
  //     handleChunkLoadError(() =>
  //       import(
  //         /* webpackChunkName: "learning-unit--grant" */
  //         '../views/GrantCertificate/PageGrantCertificate'
  //       )
  //     ),
  //   meta: { authorize: [ROLE.teacher] }
  // },
  {
    path: '/all-messages',
    name: 'all-messages',
    component: () =>
      handleChunkLoadError(() =>
        import(
          /* webpackChunkName: "messages--all" */
          '../views/MessageAll/PageMessageAll'
        )
      ),
    meta: { authorize: [ROLE.learner, ROLE.teacher] }
  },
  {
    path: '/learning-unit/:learningUnitId/section/:sectionId/direct-message/:learnerId',
    name: 'learning-unit/section/direct-message',
    component: () =>
      handleChunkLoadError(() =>
        import(
          /* webpackChunkName: "messages--direct" */
          '../views/MessageDirect/PageMessageDirect'
        )
      )
    // meta: { authorize: [ROLE.learner, ROLE.teacher] },
  },
  {
    path: '/learning-unit/:learningUnitId/all-information',
    name: 'learning-unit/all-information',
    component: () =>
      handleChunkLoadError(() =>
        import(
          /* webpackChunkName: "learning-unit--public-info" */
          '../views/Information/PageInformation'
        )
      ),
    meta: { isPublic: true }
  },
  {
    path: '/review-learning-unit',
    name: 'review-learning-unit',
    component: () =>
      handleChunkLoadError(() =>
        import(
          /* webpackChunkName: "learning-unit--reviewer" */
          '../views/ReviewLearningUnit/PageReviewLearningUnitList'
        )
      ),
    meta: { authorize: [ROLE.reviewer] }
  },
  {
    path: '/review-learning-unit/edit/:learningUnitId',
    name: 'review-learning-unit/edit',
    component: () =>
      handleChunkLoadError(() =>
        import(
          /* webpackChunkName: "learning-unit--reviewer-edit" */
          '../views/LearningUnitReview/PageLearningUnitReview'
        )
      ),
    meta: { authorize: [ROLE.reviewer] }
  },
  {
    path: '/review-learning-unit/edit-mc/:learningUnitId',
    name: 'review-learning-unit/edit-mc',
    component: () =>
      handleChunkLoadError(() =>
        import(
          /* webpackChunkName: "learning-unit--reviewer-edit-mc" */
          '../views/LearningUnit/PageEditMicroCredentialReview'
        )
      ),
    meta: {
      authorize: [ROLE.reviewer]
    }
  },
  {
    path: '/review-learning-unit/edit/:learningUnitId/teaching-feed/:sectionId',
    name: 'review-learning-unit/teaching-feed',
    component: () =>
      handleChunkLoadError(() =>
        import(
          /* webpackChunkName: "learning-unit--reviewer-teaching-feed" */
          '../views/LearningUnitReview/PageReviewTeachingFeed'
        )
      ),
    meta: { authorize: [ROLE.reviewer] }
  },
  {
    path: '/review-learning-unit/:learningUnitId/request-changes',
    name: 'review-learning-unit/request-changes',
    component: () =>
      handleChunkLoadError(() =>
        import(
          /* webpackChunkName: "learning-unit--reviewer-request-changes" */
          '../views/LearningUnitReview/PageRequestChanges'
        )
      ),
    meta: { authorize: [ROLE.reviewer] }
  },
  {
    path: '/grant-obem',
    name: 'grant-obem',
    component: () =>
      handleChunkLoadError(() =>
        import(
          /* webpackChunkName: "learning-unit--evidence-submission-grant" */
          '../views/EvidenceSubmission/PageReviewedEvidence'
        )
      ),
    meta: { authorize: [ROLE.issuer], redirectPrevious: true }
  },
  {
    path: '/forgot-password',
    name: 'forgot-password',
    component: () =>
      handleChunkLoadError(() =>
        import(
          /* webpackChunkName: "auth--forgot-password" */
          '../views/ResetPassword/PageForgotPassword'
        )
      ),
    meta: { isPublic: true }
  },
  {
    path: '/reset-password',
    name: 'reset-password',
    component: () =>
      handleChunkLoadError(() =>
        import(
          /* webpackChunkName: "auth--reset-password" */
          '../views/ResetPassword/PageResetPassword'
        )
      ),
    meta: { isPublic: true }
  },
  {
    path: '/not-found',
    name: 'not-found',
    component: () =>
      handleChunkLoadError(() =>
        import(
          /* webpackChunkName: "error--404" */
          '../views/Error/Page404Notfound'
        )
      ),
    meta: { isPublic: true }
  },
  {
    path: '/user-dashboard',
    name: 'user-dashboard',
    component: () =>
      handleChunkLoadError(() =>
        import(
          /* webpackChunkName: "auth--user-dashboard" */
          '../views/Dashboard/PageUserDashboard'
        )
      ),
    meta: { authorize: [ROLE.learner, ROLE.teacher, ROLE.designer] }
  },
  {
    path: '/badge-collection',
    name: 'badge-collection',
    component: () =>
      handleChunkLoadError(() =>
        import(
          /* webpackChunkName: "auth--badge-collection" */
          '../views/Badge/PageBadgeCollection'
        )
      ),
    meta: { authorize: [ROLE.learner, ROLE.teacher, ROLE.designer] }
  },
  {
    path: '/badge-detail/:code',
    name: 'badge-detail',
    component: () =>
      handleChunkLoadError(() =>
        import(
          /* webpackChunkName: "badg--detail" */
          '../views/Badge/PageBadgeDetail'
        )
      ),
    meta: { isPublic: true }
  },
  {
    path: '/learning-unit/:learningUnitId/forums/:forumId',
    name: 'forum',
    component: () =>
      handleChunkLoadError(() =>
        import(
          /* webpackChunkName: "learning-unit--forum" */
          '../views/Feed/PageForum'
        )
      ),
    meta: {
      authorize: [ROLE.designer, ROLE.learner, ROLE.reviewer],
      learningUnit: { isMember: true },
      application: { mode: MODE.learn_verify }
    }
  },
  {
    path: '/learning-unit/:learningUnitId/forums/:forumId/edit',
    name: 'forum-edit',
    component: () =>
      handleChunkLoadError(() =>
        import(
          /* webpackChunkName: "learning-unit--forum-edit" */
          '../views/Feed/PageEditForum'
        )
      ),
    meta: {
      authorize: [ROLE.designer, ROLE.learner, ROLE.reviewer],
      learningUnit: { isMember: true }
    }
    // meta: { authorize: [ROLE.learner, ROLE.teacher] },
  },
  {
    path: '/checkout/option/:learningUnitOptionId',
    name: 'checkout-option',
    component: () =>
      handleChunkLoadError(() =>
        import(
          /* webpackChunkName: "store--checkout" */
          '../views/Payment/PageCheckoutLearningUnitOption'
        )
      )
    // meta: {
    //    featureFlag: [FEATURE.purchase_buynow]
    // }
  },
  {
    path: '/cart',
    name: 'cart-item',
    component: () =>
      handleChunkLoadError(() =>
        import(
          /* webpackChunkName: "store--cart" */
          '../views/Cart/PageCartItem'
        )
      ),
    meta: {
      authorize: [ROLE.learner],
      featureFlag: [FEATURE.cart]
    }
    // beforeEnter: () => {
    //   return false;
    // }
  },
  {
    path: '/payment-success/:orderId',
    name: 'payment-success',
    component: () =>
      handleChunkLoadError(() =>
        import(
          /* webpackChunkName: "store--payment-success" */
          '../views/Payment/PagePaymentSuccess'
        )
      ),
    meta: {
      authorize: ['*']
    }
  },
  {
    path: '/payment-decline',
    name: 'payment-decline',
    component: () =>
      handleChunkLoadError(() =>
        import(
          /* webpackChunkName: "store--payment-decline" */
          '../views/Payment/PagePaymentDecline'
        )
      ),
    meta: {
      authorize: ['*']
    }
  },
  {
    path: '/purchase-detail/:orderId',
    name: 'purchase-detail',
    component: () =>
      handleChunkLoadError(() =>
        import(
          /* webpackChunkName: "store--purchase-detail" */
          '../views/Payment/PagePurchaseDetail'
        )
      ),
    meta: {
      authorize: ['*']
    }
  },
  {
    path: '/purchase',
    name: 'purchase',
    component: () =>
      handleChunkLoadError(() =>
        import(
          /* webpackChunkName: "store--purchase-history" */
          '../views/Payment/PagePurchaseHistory'
        )
      ),
    meta: {
      authorize: [ROLE.learner]
      // featureFlag: [FEATURE.purchase_buynow]
    }
  },
  {
    path: '/faq/community-rules-and-guidelines',
    name: 'community-rules',
    component: () =>
      handleChunkLoadError(() =>
        import(
          /* webpackChunkName: "faq--community-rules" */
          '../views/Feed/PageCommunityRules'
        )
      ),
    meta: { isPublic: true }
  },
  {
    path: '/faq/payment-policy',
    name: 'payment-policy',
    component: () =>
      handleChunkLoadError(() =>
        import(
          /* webpackChunkName: "faq--payment-policy" */
          '../views/Payment/PagePaymentPolicy'
        )
      ),
    meta: { isPublic: true, featureFlag: [FEATURE.cart] }
  },
  {
    path: '/verify-change-email',
    name: 'verify-change-email',
    component: () =>
      handleChunkLoadError(() =>
        import(
          /* webpackChunkName: "reqeust-change-email" */
          '../views/ChangeRequest/VerifyChangeEmail/PageVerifyChangeEmail'
        )
      ),
    meta: { isPublic: true, layout: 'no' }
  },
  {
    path: '/on-boarding',
    name: 'on-boarding',
    component: () =>
      handleChunkLoadError(() =>
        import(
          /* webpackChunkName: "on-boarding" */
          '../views/Onboarding/PageOnboarding'
        )
      ),
    meta: {
      authorize: ['*']
    }
  },
  {
    path: '/stack-managing/create',
    name: 'create-stack',
    component: () =>
      handleChunkLoadError(() =>
        import(
          /* webpackChunkName: "on-boarding" */
          '../views/Stack/PageCreateStack'
        )
      ),
    meta: {
      authorize: ['*']
    }
  },
  {
    path: '/stack-managing/edit/:id',
    name: 'edit-stack',
    component: () =>
      handleChunkLoadError(() =>
        import(
          /* webpackChunkName: "on-boarding" */
          '../views/Stack/PageEditStack'
        )
      ),
    meta: {
      authorize: ['*']
    }
  },
  {
    path: '/stack-managing',
    name: 'stack-managing',
    component: () =>
      handleChunkLoadError(() =>
        import(
          /* webpackChunkName: "stack-managing" */
          '../views/Stack/PageStackIndex.vue'
        )
      ),
    meta: { authorize: [ROLE.reviewer] }
  },
  {
    path: '/stack-detail/:stackId',
    name: 'stack-detail',
    component: () =>
      handleChunkLoadError(() =>
        import(
          /* webpackChunkName: "stack-detail" */
          '../views/Stack/PageStackDetail.vue'
        )
      ),
    meta: { isPublic: true }
  },
  // {
  //   path: "/test",
  //   name: "test",
  //   component: () => import("./examples/ShowCriterion"),
  // },
  // otherwise redirect to home
  { path: '*', component: () => import('../views/Error/Page404Notfound') }
  // { path: "*", component: () => import("../views/home.vue") },
];
